// =============================================================================
// RaiseDefectModal.tsx — HOP Slice 4. Raise a site defect against a project
// (and optionally a HOP pack / a specific photo). Category, severity, who goes
// back, ETA, description. Creates a site_defects row + an initial history entry.
// Reusable: pass projectId/packId/assetId to pre-scope it, or let the user pick.
// =============================================================================

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { X, AlertTriangle, ClipboardCheck } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useRequireAuth } from "@/lib/auth";
import { Button } from "@/components/ui";

interface Props {
  projectId?: string;
  packId?: string;
  assetId?: string;
  onClose: () => void;
  onCreated?: () => void;
}

const CATEGORIES = ["gland", "ground", "rigging", "civil", "structural", "electrical", "other"];
const SEVERITIES = ["minor", "major", "critical"];

export default function RaiseDefectModal({ projectId: fixedProject, packId, assetId, onClose, onCreated }: Props) {
  const user = useRequireAuth();
  const [projectId, setProjectId] = useState(fixedProject ?? "");
  const [category, setCategory]   = useState("other");
  const [severity, setSeverity]   = useState("minor");
  const [title, setTitle]         = useState("");
  const [description, setDescription] = useState("");
  const [assignee, setAssignee]   = useState("");
  const [dueDate, setDueDate]     = useState("");
  const [busy, setBusy]           = useState(false);
  const [error, setError]         = useState<string | null>(null);

  const { data: projects = [] } = useQuery({
    queryKey: ["defect_projects"],
    enabled: !fixedProject,
    queryFn: async () => {
      const { data } = await supabase.from("projects")
        .select("project_id, project_code, project_name, site:site_id ( site_name )")
        .not("status", "in", "(cancelled)").order("project_code", { ascending: false }).limit(300);
      return (data ?? []) as any[];
    },
  });

  const { data: employees = [] } = useQuery({
    queryKey: ["defect_employees"],
    queryFn: async () => {
      const { data } = await supabase.from("employees")
        .select("employee_id, first_name, last_name").eq("is_active", true).order("first_name");
      return (data ?? []) as any[];
    },
  });

  // Resolve site_id from the project (defects roll up per site).
  async function siteForProject(pid: string): Promise<string | null> {
    const { data } = await supabase.from("projects").select("site_id").eq("project_id", pid).maybeSingle();
    return (data as any)?.site_id ?? null;
  }

  async function create() {
    setBusy(true); setError(null);
    try {
      if (!projectId) throw new Error("Pick a project.");
      if (!description.trim()) throw new Error("Add a description of the defect.");
      const site_id = await siteForProject(projectId);
      const { data, error: e } = await supabase.from("site_defects").insert({
        project_id: projectId,
        site_id,
        pack_id: packId ?? null,
        asset_id: assetId ?? null,
        category, severity,
        title: title.trim() || null,
        description: description.trim(),
        assigned_to_employee_id: assignee || null,
        due_date: dueDate || null,
        raised_by: user?.user_id ?? null,
        source: "internal",
      }).select("defect_id").single();
      if (e || !data) throw new Error(e?.message ?? "Couldn't raise the defect.");
      await supabase.from("defect_status_history").insert({
        defect_id: (data as any).defect_id, changed_by: user?.user_id ?? null,
        old_status: null, new_status: "open", note: "Raised",
      });
      onCreated?.();
      onClose();
    } catch (e: any) { setError(e?.message ?? "Couldn't raise the defect."); }
    finally { setBusy(false); }
  }

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-end md:items-center md:justify-center p-0 md:p-4" onClick={onClose}>
      <div className="bg-ink-card border-t md:border border-line w-full md:max-w-lg md:rounded-lg max-h-[92vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-line flex items-center justify-between">
          <div className="flex items-center gap-2"><AlertTriangle size={18} className="text-warn" /><h3 className="text-[16px] font-semibold text-text">Raise a defect</h3></div>
          <button onClick={onClose} className="text-text-muted hover:text-text p-1"><X size={18} /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-3">
          {!fixedProject && (
            <label className="block text-[11px] text-text-muted">Project
              <select value={projectId} onChange={e => setProjectId(e.target.value)} className="mt-1 w-full bg-ink border border-line rounded px-2.5 py-2 text-[13px] text-text">
                <option value="">Pick a project…</option>
                {projects.map((p: any) => <option key={p.project_id} value={p.project_id}>{[p.project_code, p.project_name, p.site?.site_name].filter(Boolean).join(" · ")}</option>)}
              </select>
            </label>
          )}
          {packId && <div className="text-[11px] text-text-muted">Linked to this HOP pack{assetId ? " + photo" : ""}.</div>}

          <div className="grid grid-cols-2 gap-3">
            <label className="block text-[11px] text-text-muted">Category
              <select value={category} onChange={e => setCategory(e.target.value)} className="mt-1 w-full bg-ink border border-line rounded px-2.5 py-2 text-[13px] text-text capitalize">
                {CATEGORIES.map(c => <option key={c} value={c} className="capitalize">{c}</option>)}
              </select>
            </label>
            <label className="block text-[11px] text-text-muted">Severity
              <select value={severity} onChange={e => setSeverity(e.target.value)} className="mt-1 w-full bg-ink border border-line rounded px-2.5 py-2 text-[13px] text-text capitalize">
                {SEVERITIES.map(s => <option key={s} value={s} className="capitalize">{s}</option>)}
              </select>
            </label>
          </div>

          <label className="block text-[11px] text-text-muted">Title (optional)
            <input value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. Sector S2 mount missing earth bond"
              className="mt-1 w-full bg-ink border border-line rounded px-2.5 py-2 text-[13px] text-text" />
          </label>

          <label className="block text-[11px] text-text-muted">Description
            <textarea value={description} onChange={e => setDescription(e.target.value)} rows={3} placeholder="What's wrong + what needs doing to fix it."
              className="mt-1 w-full bg-ink border border-line rounded px-2.5 py-2 text-[13px] text-text" />
          </label>

          <div className="grid grid-cols-2 gap-3">
            <label className="block text-[11px] text-text-muted">Who goes back
              <select value={assignee} onChange={e => setAssignee(e.target.value)} className="mt-1 w-full bg-ink border border-line rounded px-2.5 py-2 text-[13px] text-text">
                <option value="">Unassigned</option>
                {employees.map((e: any) => <option key={e.employee_id} value={e.employee_id}>{e.first_name} {e.last_name}</option>)}
              </select>
            </label>
            <label className="block text-[11px] text-text-muted">ETA to resolve
              <input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} className="mt-1 w-full bg-ink border border-line rounded px-2.5 py-2 text-[13px] text-text" />
            </label>
          </div>

          {error && <div className="p-3 rounded bg-red-900/30 border border-red-900/50 text-[12px] text-red-200 flex items-start gap-2"><AlertTriangle size={12} className="mt-0.5" /><span>{error}</span></div>}
        </div>

        <div className="px-5 py-4 border-t border-line flex items-center justify-end gap-2">
          <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button icon={ClipboardCheck} onClick={create} disabled={busy || !description.trim() || !projectId}>{busy ? "Raising…" : "Raise defect"}</Button>
        </div>
      </div>
    </div>
  );
}
